import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import ThemeToggle from './ThemeToggle';
import './SiteNav.css';

/**
 * The site's one navigation bar.
 *
 * Until now only the homepage and DynamicLandingPage had it; every other page
 * rolled its own header, ranging from seven links (Library) down to ZERO
 * (/translate). /humanize had a lone "← Back to Generator", which made a page
 * that is itself IN the main nav feel like a different product — you could
 * arrive from the nav and then have no way onward.
 *
 * Two details that stop this being a copy-paste of the homepage markup:
 *
 * 1. The section links (#how, #features, #showcase, #pricing) only resolve on
 *    the homepage. Anywhere else they have to be "/#how" or they silently do
 *    nothing — the single most likely way a shared nav breaks.
 *
 * 2. The active item was hardcoded to Library. It now comes from the router.
 *
 * `variant` covers the one real behavioural difference. The homepage nav floats
 * transparent over the hero and only gains a background on scroll; everywhere
 * else the nav sits above content and needs to be solid immediately, with a
 * spacer so the fixed bar does not cover the first element on the page.
 */
interface SiteNavProps {
  variant?: 'overlay' | 'solid';
  /** Homepage passes its own CTA target so a typed concept survives signup. */
  ctaHref?: string;
  onCtaClick?: () => void;
}

const SiteNav: React.FC<SiteNavProps> = ({ variant = 'solid', ctaHref, onCtaClick }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobOpen, setMobOpen] = useState(false);

  const isLoggedIn = !!user;
  const onHome = location.pathname === '/';
  // Root-relative on every other page, or the browser just sits still.
  const section = (id: string) => (onHome ? `#${id}` : `/#${id}`);
  const isActive = (path: string) => location.pathname === path;

  useEffect(() => {
    if (variant !== 'overlay') return;
    const onScroll = () => setScrolled(window.scrollY > 10);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [variant]);

  // Close the drawer on navigation, or it stays open behind the new page.
  useEffect(() => setMobOpen(false), [location.pathname]);

  const solid = variant === 'solid' || scrolled;
  const writeHref = ctaHref || (isLoggedIn ? '/bookwriter' : '/register');

  const close = () => setMobOpen(false);

  return (
    <>
      <nav className={`hp-nav${solid ? ' scrolled' : ''}`} role="navigation" aria-label="Main navigation">
        <Link to="/" className="nav-logo" aria-label="ShakespeareAI Home">
          <img src="/shakespeareAI-logo-nobg.png" alt="ShakespeareAI" decoding="async" style={{ width: '36px', height: '36px' }} />
          Shakespeare<em>AI</em>
        </Link>

        <div className="nav-links">
          <a href={section('how')}>How It Works</a>
          <Link to="/features" className={isActive('/features') ? 'active' : undefined}>Features</Link>
          <a href={section('showcase')}>Book Showcase</a>
          <a href={section('pricing')}>Pricing</a>
          <a href="/blog">Blog</a>
          <Link to="/about" className={isActive('/about') ? 'active' : undefined}>About</Link>
          <Link to="/humanize" className={isActive('/humanize') ? 'active' : undefined}>✨ AI Humanizer</Link>
          <Link to="/translate" className={isActive('/translate') ? 'active' : undefined}>🌍 Translator</Link>
          <Link to="/library" className={isActive('/library') ? 'active' : undefined}>📚 Library</Link>
          <Link to="/book-signal" className={isActive('/book-signal') ? 'active' : undefined}>📊 Book Signal</Link>
          <Link to="/affiliate" className={isActive('/affiliate') ? 'active' : undefined}>🤝 Affiliate</Link>
          {isLoggedIn ? (
            <>
              <Link to="/account" className={isActive('/account') ? 'active' : undefined}>⚙️ My Account</Link>
              <Link to="/my-books" className={isActive('/my-books') ? 'active' : undefined}>📖 My Books</Link>
            </>
          ) : (
            <Link to="/login">📖 My Books</Link>
          )}
        </div>

        <div className="nav-right">
          <ThemeToggle />
          {isLoggedIn ? (
            <>
              <Link to="/my-books" className="nav-ghost">Dashboard</Link>
              <button
                onClick={() => { logout(); navigate('/'); }}
                className="nav-ghost nav-ghost-btn"
              >
                Logout
              </button>
              <Link to={writeHref} onClick={onCtaClick} className="nav-cta">Start Writing →</Link>
            </>
          ) : (
            <>
              <Link to="/login" className="nav-ghost">Sign In</Link>
              <Link to={writeHref} onClick={onCtaClick} className="nav-cta">Start Writing →</Link>
            </>
          )}
        </div>

        <button
          className="mob-menu-btn"
          onClick={() => setMobOpen(!mobOpen)}
          aria-label="Open menu"
          aria-expanded={mobOpen}
        >
          <svg width="22" height="16" viewBox="0 0 22 16" fill="none" aria-hidden="true">
            <rect y="0" width="22" height="2" rx="1" fill="currentColor" />
            <rect y="7" width="16" height="2" rx="1" fill="currentColor" />
            <rect y="14" width="22" height="2" rx="1" fill="currentColor" />
          </svg>
        </button>
      </nav>

      <div className={`mob-nav${mobOpen ? ' show' : ''}`} role="navigation" aria-label="Mobile navigation">
        <a href={section('how')} onClick={close}>How It Works</a>
        <Link to="/features" onClick={close}>Features</Link>
        <a href={section('showcase')} onClick={close}>Book Showcase</a>
        <a href={section('pricing')} onClick={close}>Pricing</a>
        <a href="/blog" onClick={close}>Blog</a>
        <Link to="/about" onClick={close}>About</Link>
        <Link to="/humanize" onClick={close}>✨ AI Humanizer</Link>
        <Link to="/translate" onClick={close}>🌍 Translator</Link>
        <Link to="/library" onClick={close}>📚 Library</Link>
        <Link to="/book-signal" onClick={close}>📊 Book Signal</Link>
        <Link to="/affiliate" onClick={close}>🤝 Affiliate</Link>
        {isLoggedIn ? (
          <>
            <Link to="/my-books" onClick={close}>📖 My Books</Link>
            <Link to="/account" onClick={close}>⚙️ My Account</Link>
            <button
              className="mob-logout"
              onClick={() => { logout(); navigate('/'); close(); }}
            >
              Logout
            </button>
          </>
        ) : (
          <Link to="/login" onClick={close}>Sign In</Link>
        )}
        <Link to={writeHref} className="mob-cta" onClick={() => { onCtaClick?.(); close(); }}>Start Writing →</Link>
      </div>

      {/* The bar is position:fixed. Without this the first element of every
          page sits underneath it. The homepage hero is designed to run behind
          the transparent overlay, so it opts out. */}
      {variant === 'solid' && <div className="site-nav-spacer" aria-hidden="true" />}
    </>
  );
};

export default SiteNav;
